'use client';

import { useState, useEffect, useRef } from 'react';
import { getRepId, getRepFullName, getRepTitle } from '@/app/lib/reps';
import { getTotalPrice, applyPromoToTotal, type PromoCode } from '@/app/lib/pricing';
import { getStoredAttribution } from '@/app/components/AttributionCapture';

export const MAX_CHARS = Number(process.env.NEXT_PUBLIC_MAX_CHARS) || 270;

export interface GeocodedAddress { line1: string; city: string; state: string; zip: string }

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
export const DRAFT_KEY_PREFIX = 'ptc_draft:';

export interface RepFlowOptions {
  // localStorage key for draft persistence; omit to disable persistence
  draftKey?: string;
  // /m pages keep the campaign message server-sourced, so they skip persisting it
  persistMessage?: boolean;
  // called once after a saved draft is restored (receives the raw draft, incl. extras like step)
  onDraftRestored?: (draft: any) => void;
  // page-specific fields persisted alongside hook state (e.g. { step })
  draftExtras?: Record<string, unknown>;
}

// Shared search → cart → promo → checkout logic for the postcard flows
// (home/start/abroad SearchFlow and campaign /m pages). Pages own their own
// layout, step navigation, and any page-specific checkout extras.
export function useRepFlow(options: RepFlowOptions = {}) {
  const { draftKey, persistMessage = true, onDraftRestored, draftExtras } = options;
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState<'address' | 'name'>('address');
  const [address, setAddress] = useState('');
  const [repName, setRepName] = useState('');

  const [cart, setCart] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [geocodedAddress, setGeocodedAddress] = useState<GeocodedAddress | null>(null);

  const [message, setMessage] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<{ promo: PromoCode; label: string } | null>(null);
  const [promoError, setPromoError] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  // Opt-ins start unticked: pre-checked consent boxes hurt trust and poison
  // the newsletter list with people who never meant to subscribe.
  const [okToContact, setOkToContact] = useState(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
  const [govLevels, setGovLevels] = useState<Set<'federal' | 'state'>>(new Set(['federal']));
  const [stateJurisdiction, setStateJurisdiction] = useState('');

  const [searchError, setSearchError] = useState('');
  const [searchNotice, setSearchNotice] = useState('');
  const [checkoutError, setCheckoutError] = useState('');

  const upsellShownAtRef = useRef<number | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const skipFirstPersistRef = useRef(true);

  const cartIds = cart.map((r: any) => getRepId(r));
  const hasUnadded = searchResults.some((r: any) => !cartIds.includes(getRepId(r)));

  // ── Draft persistence ───────────────────────────────────────────────────────
  // Restore on mount. URL params (?name=, ?promo=) take precedence over drafts.
  useEffect(() => {
    if (!draftKey) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('name') || params.get('promo')) return;
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft.savedAt || Date.now() - draft.savedAt > DRAFT_TTL_MS) {
        localStorage.removeItem(draftKey);
        return;
      }
      const hasContent = (draft.cart?.length > 0) || (persistMessage && (draft.message || '').trim());
      if (!hasContent) return;
      if (Array.isArray(draft.cart)) setCart(draft.cart);
      if (Array.isArray(draft.searchResults)) setSearchResults(draft.searchResults);
      if (persistMessage && typeof draft.message === 'string') setMessage(draft.message);
      if (typeof draft.address === 'string') setAddress(draft.address);
      if (typeof draft.repName === 'string') setRepName(draft.repName);
      if (draft.searchMode === 'name' || draft.searchMode === 'address') setSearchMode(draft.searchMode);
      if (Array.isArray(draft.govLevels) && draft.govLevels.length > 0) setGovLevels(new Set(draft.govLevels));
      if (typeof draft.stateJurisdiction === 'string') setStateJurisdiction(draft.stateJurisdiction);
      setDraftRestored(true);
      window.umami?.track('draft_restored');
      onDraftRestored?.(draft);
    } catch { /* corrupted draft — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save on change (skip the initial mount so an empty first render can't wipe a draft)
  const extrasSerialized = JSON.stringify(draftExtras ?? {});
  useEffect(() => {
    if (!draftKey) return;
    if (skipFirstPersistRef.current) { skipFirstPersistRef.current = false; return; }
    try {
      if (cart.length === 0 && !(persistMessage && message.trim())) {
        localStorage.removeItem(draftKey);
        return;
      }
      localStorage.setItem(draftKey, JSON.stringify({
        savedAt: Date.now(),
        cart,
        searchResults,
        message: persistMessage ? message : '',
        address,
        repName,
        searchMode,
        govLevels: Array.from(govLevels),
        stateJurisdiction,
        ...(draftExtras ?? {}),
      }));
    } catch { /* storage full/unavailable — non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, searchResults, message, address, repName, searchMode, govLevels, stateJurisdiction, extrasSerialized, draftKey, persistMessage]);

  // "Start fresh": drop the saved draft and reset flow state
  const discardDraft = () => {
    try { if (draftKey) localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setCart([]);
    setSearchResults([]);
    if (persistMessage) setMessage('');
    setAddress('');
    setRepName('');
    setDraftRestored(false);
    window.umami?.track('draft_discarded');
  };

  // ── Search ──────────────────────────────────────────────────────────────────
  const runSearch = async (): Promise<any[] | null> => {
    setLoading(true);
    setSearchError('');
    setSearchNotice('');
    try {
      const levels = Array.from(govLevels);
      let payload: Record<string, unknown>;
      if (searchMode === 'name') {
        if (govLevels.has('state') && !stateJurisdiction) {
          setSearchError('Please select a state to search state legislators by name.');
          return null;
        }
        payload = { name: repName, levels, ...(govLevels.has('state') ? { jurisdiction: stateJurisdiction } : {}) };
      } else {
        payload = { address, levels };
      }

      const response = await fetch('/api/reps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        if (data.stateNotice) setSearchNotice(data.stateNotice);
        if (data.geocodedAddress) setGeocodedAddress(data.geocodedAddress);
        const allResults = [
          ...(data.federalLegislators || []),
          ...(data.stateLegislators || []),
        ];
        setSearchResults(allResults);
        if (typeof window.umami !== 'undefined' && data.stateLegislators?.length > 0) {
          window.umami.track('state_lookup_completed', {
            jurisdiction: stateJurisdiction || 'geo',
            result_count: data.stateLegislators.length,
          });
        }
        if (allResults.length === 0) {
          setSearchError(searchMode === 'name'
            ? 'No representatives found by that name. Check the spelling and try again.'
            : 'No representatives found for that address. Try including your city and ZIP code.');
        }
        return allResults;
      } else {
        setSearchError(data.error || 'Something went wrong searching for reps. Please try again.');
        return null;
      }
    } catch (error) {
      console.error(error);
      setSearchError('Could not reach the server. Check your connection and try again.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ── Cart ────────────────────────────────────────────────────────────────────
  const addToCart = (rep: any) => {
    const id = getRepId(rep);
    if (!id || cartIds.includes(id)) return;
    const prevSize = cart.length;
    const newSize = prevSize + 1;
    if (upsellShownAtRef.current && Date.now() - upsellShownAtRef.current < 30000) {
      window.umami?.track('upsell_prompt_converted', { from_count: prevSize, to_count: newSize });
    }
    if ([3, 5, 8].includes(newSize)) {
      window.umami?.track('cart_tier_reached', { card_count: newSize, total_cents: getTotalPrice(newSize) });
    }
    setCart(prev => [...prev, rep]);
  };

  const addAllToCart = (legislators: any[]) => {
    setCart(prev => {
      const existingIds = prev.map((r: any) => getRepId(r));
      const toAdd = legislators.filter((r: any) => getRepId(r) && !existingIds.includes(getRepId(r)));
      return [...prev, ...toAdd];
    });
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter((r: any) => getRepId(r) !== id));
  };

  const markUpsellShown = () => { upsellShownAtRef.current = Date.now(); };

  // ── Message ─────────────────────────────────────────────────────────────────
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_CHARS) setMessage(text);
  };

  // ── Promo ───────────────────────────────────────────────────────────────────
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    setAppliedPromo(null);
    try {
      const res = await fetch('/api/promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode, quantity: cart.length }),
      });
      const data = await res.json();
      if (data.valid) {
        setAppliedPromo({ promo: data.promo, label: data.label });
      } else {
        setPromoError(data.error || 'Invalid promo code');
      }
    } catch {
      setPromoError('Could not validate code. Try again.');
    } finally {
      setPromoLoading(false);
    }
  };

  // Apply a promo code arriving via URL param (?promo=...)
  const applyUrlPromo = (code: string) => {
    setPromoCode(code);
    setShowPromoInput(true);
    fetch('/api/promo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, quantity: 1 }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.valid) setAppliedPromo({ promo: data.promo, label: data.label });
      })
      .catch(() => {});
  };

  const cartTotal = (qty: number) => appliedPromo
    ? applyPromoToTotal(getTotalPrice(qty), appliedPromo.promo, qty)
    : getTotalPrice(qty);

  // ── Checkout ────────────────────────────────────────────────────────────────
  // `extras` carries page-specific payload fields (vsSlugA/B, abroad, votingAddress).
  const handleCheckout = async (extras: Record<string, unknown> = {}) => {
    setCheckoutError('');
    setIsCheckingOut(true);

    const selectedRepIds = cart.map((r: any) => getRepId(r));
    const repNames = cart.map((rep: any) => {
      const title = getRepTitle(rep).replace('U.S. ', '');
      return `${title} ${getRepFullName(rep)}`.trim();
    });
    const repTypes = cart.map((rep: any) => {
      if (rep?.level === 'state') return '';
      return rep?.type === 'senator' ? 'S' : 'H';
    });
    const repAddresses = cart.map((rep: any) => {
      if (rep?.level === 'state') return rep.capitol_address ?? null;
      return null;
    });

    if (typeof window.umami !== 'undefined') {
      window.umami?.track('checkout_started', { card_count: cart.length });
      cart.filter((r: any) => r?.level === 'state').forEach((r: any) => {
        window.umami?.track('state_postcard_sent', { jurisdiction: r.jurisdiction, chamber: r.chamber });
      });
    }

    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message, selectedRepIds, repNames, repTypes, repAddresses, address,
          promoCode: appliedPromo ? promoCode : '',
          okToContact, subscribeNewsletter,
          attrib: getStoredAttribution(),
          ...extras,
        }),
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        setCheckoutError(data.error || 'Checkout failed. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setCheckoutError('A network error occurred during checkout. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  return {
    // state
    loading, searchMode, setSearchMode, address, setAddress, repName, setRepName,
    cart, setCart, searchResults, setSearchResults, geocodedAddress,
    message, setMessage, isCheckingOut,
    promoCode, setPromoCode, appliedPromo, setAppliedPromo, promoError, setPromoError,
    showPromoInput, setShowPromoInput, promoLoading,
    okToContact, setOkToContact, subscribeNewsletter, setSubscribeNewsletter,
    govLevels, setGovLevels, stateJurisdiction, setStateJurisdiction,
    searchError, setSearchError, searchNotice, checkoutError, setCheckoutError,
    draftRestored,
    // derived
    cartIds, hasUnadded, cartTotal,
    // actions
    runSearch, addToCart, addAllToCart, removeFromCart, markUpsellShown,
    handleMessageChange, handleApplyPromo, applyUrlPromo, handleCheckout, discardDraft,
  };
}
